import { getIsElementInViewport } from '@core/js/utils/scrollUtils';
import { error, warn } from '@core/js/utils/logger';

import { handleUtagEvent } from '@core/js/actions/initialActions';

/**
 * How long to poll for the Tealium scripts in ms
 * before timing out
 * @type {Number}
 */
export const UTAG_POLL_TIME = 5000;

export const GA_INVIEW_DELAY = 100;

/**
 * Get the Tealium UDO model
 * @return {Object}     Tealium UDO Model
 */
export function getUtagModel() {
    return _.get(window, 'utag.data', window.utag_data);
}

/**
 * Is this a checkout page?
 * @return {Boolean} True, if this is a checkout page; false, otherwise.
 */
export function isCheckout() {
    return window.location.pathname.includes('/checkout');
}

/**
 * Are the Tealium scripts loaded?
 * @return {Boolean} True, if Tealium is loaded; false, otherwise.
 */
export function utagIsLoaded() {
    return Boolean(window.utag);
}

/**
 * Set a Tealium link analytics
 * @param  {Object}  data     New Tealium link analytics
 * @returns {undefined} returns nothing
 */
export function utagLink(data) {
    const safeData = getGADefaults(data);
    if (window.utag) {
        window.utag.link(safeData);
    } else {
        warn('window.utag is undefined');
    }
}

/**
 * Decorate link data with empty GA values to prevent duplicate calls
 * to collect endpoint
 * @param  {Object}  data   link data
 * @returns {Object} data merged with empty string defaults
 */
export function getGADefaults(data) {
    const defaults = {
        'event_category': data.event_category || '',
        'event_action': data.event_action || '',
        'event_label': data.event_label || '',
        'ga_transport_method': 'beacon'
    };

    const safeData = _.merge(data, defaults);

    return safeData;
}

/**
 * Merge new values from manual view calls into the data layer
 *
 * @param   {object}    data     updated values sent with view event
 * @returns {undefined}
 */
export function mergeDataLayer(data) {
    const udo = _.get(window, 'urbn.vendor', {});

    if (!window.urbn) {
        window.urbn = {};
    }

    window.urbn.vendor = Object.assign({}, udo, data);
}

/**
 * Set the Tealium view analytics
 * @param  {Object} data New Tealium view analytics
 * @return {Object}      New Tealium UDO model
 */
export function utagView(data) {
    // Add current utag data to data layer object
    mergeDataLayer(data);

    const utagData = getUtagModel();
    let mergedUtagData;
    let safeData;

    if (window.utag && utagData) {
        safeData = getGADefaults(data);
        mergedUtagData = Object.assign({}, utagData, safeData);
        window.utag.view(mergedUtagData);
    } else {
        error('utagView: utag is undefined');
    }

    return mergedUtagData;
}

/**
 * "Translate" an analytics slug
 * @param {object} state Redux state
 * @param {string} slug slug to translate
 * @returns {string} analytics text looked up from the analytics container by the slug
 */
export function translateAnalytics(state, slug) {
    return _.get(state, `core.localizations.containers.analyticsContainer.${slug}`, '');
}

export function getProductSellingPrice(product) {
    return (_.get(product, 'skuInfo.hasMarkdown')
        ? [`${_.get(product, 'skuInfo.salePriceLow', '')}`]
        : [`${_.get(product, 'skuInfo.listPriceHigh', '')}`]);
}

export function getProductOriginalPrice(product) {
    return [`${_.get(product, 'skuInfo.listPriceHigh', '')}`];
}

/**
 * Get product sku with product style as a fallback
 * @param {object} product object
 * @returns {array} product sku if available, else product style
 */
export function getProductSku(product) {
    const sku = _.get(product, 'selectedSku.sku.skuId');
    const styleNumber = _.get(product, 'styleNumber');

    return sku ? [sku] : [styleNumber];
}

/**
 * Create data structure needed for Google Analytics events
 * @param  {String} eventCategory    GA event category
 * @param  {String} eventAction      GA event action
 * @param  {String} eventLabel       GA event label
 * @param  {Object} customDimensions optional extra key/value pairs to be sent with the GA event
 * @return {Object} GA event object to be passed to Tealium
 */
export function createGAEvent(eventCategory, eventAction, eventLabel, customDimensions = {}) {
    // since dy_noninteraction value seems to persist between events
    const defaultCustomDimensions = {
        'dy_noninteraction': false
    };

    return Object.assign({}, {
        'event_category': eventCategory,
        'event_action': eventAction,
        'event_label': eventLabel
    }, defaultCustomDimensions, customDimensions);
}

/**
 * Create data structure needed for Google Analytics events
 * @param  {Object} attributeDataset    The set of data attributes on the element we need to grab and format
 * @param {String} prefixToReplace  Prefix of the keys to strip
 * @return {Object} GA event object to be passed to Tealium
 */
export function getGALinkTrackData(attributeDataset, prefixToReplace = 'ga') {
    const gaData = Object.keys(attributeDataset).filter(function filterResults(k) {
        const isBlankKey = !attributeDataset[k];
        return k.indexOf(prefixToReplace) === 0 && !isBlankKey;
    })
    .reduce(function reduceResults(newDataset, k) {
        newDataset[k] = attributeDataset[k];
        return newDataset;
    }, {});

    const gaDataKeys = Object.keys(gaData);
    const formattedGAData = {};

    for (const key of gaDataKeys) {
        const cleanKey = _.snakeCase(key.replace(prefixToReplace, ''));
        const gaValue = gaData[key];
        formattedGAData[cleanKey] = gaValue;
    }

    return formattedGAData;
}

/**
 * Bind scroll event to check for in view elements by selector
 * @param  {String} selector    The selector of ga inview elements to track
 * @param  {Function} callback  The function to run when element is in view
 * @return {undefined} returns nothing
 */
export function bindScrollToGaInviewElements(selector, callback) {
    const isTrackedClass = 'is-tracked';
    const selectorWithExclusions = `${selector}:not(.${isTrackedClass})`;
    const allElements = document.querySelectorAll(selectorWithExclusions);
    const debouncedInViewCheck = _.debounce((e) => {
        const elements = document.querySelectorAll(selectorWithExclusions);

        if (!elements.length) {
            window.removeEventListener('scroll', debouncedInViewCheck);
            return;
        }

        _.each(elements, element => {
            const isInView = getIsElementInViewport(element);

            if (isInView) {
                callback(e, element);
                element.classList.add(isTrackedClass);
            }
        });
    }, GA_INVIEW_DELAY);

    if (allElements.length) {
        window.addEventListener('scroll', debouncedInViewCheck);
    }
}

/**
 * Fire a GA tracking event for page type analytics tracking
 * @param {object} store Redux Store
 * @param  {String} action action for GA event
 * @param  {String} label label for GA event
 * @return {undefined} returns nothing
 */
export function fireGATrackingEvent(store, action, label) {
    const pageType = _.get(store.getState(), 'core.pageType');
    const gaEvent = createGAEvent(
        pageType,
        action,
        label
    );
    store.dispatch(handleUtagEvent(gaEvent, 'link'));
}
